import React, {
  forwardRef,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import Image from 'next/image';
import { usePathname } from 'next/navigation';
import { type ProductItem } from '@/common/types/product-details.type';
import classNames from 'classnames';
import type SwiperCore from 'swiper';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import 'swiper/css/zoom';
import { Pagination, Scrollbar, Thumbs, Zoom } from 'swiper/modules';
import {
  Swiper,
  SwiperSlide,
  type SwiperClass,
  type SwiperProps,
  type SwiperRef,
} from 'swiper/react';
import DiscountContainer from '@/common/components/detail-container';
import DiagonalLine from '@/common/components/diagonal-line/diagonal-line.component';
import ShareButton from '@/common/components/share-button';
import 'swiper/css';
import ArrowLeftIcon from '~/public/assets/images/svg/arrow-left-gray.svg';

interface SwiperImageProps extends SwiperProps {
  images: ImageType[];
  discount?: number;
  outOfStock?: boolean;
  onSelectImage?: (selectedIndex: number) => void;
  onOpenGallery?: () => void;
  selectedImage?: number;
  imageClassName?: string;
  thumbClassName?: string;
  hasThumbs?: boolean;
  productDetail: Partial<ProductItem>;
}

interface ImageType {
  name?: string;
  url: string;
  uid?: string;
}

const SwiperImage = forwardRef<SwiperRef, SwiperImageProps>(
  function SwiperImage(
    {
      initialSlide = 0,
      images,
      discount,
      outOfStock,
      onSelectImage,
      selectedImage,
      onOpenGallery,
      imageClassName = '',
      thumbClassName = '',
      hasThumbs = false,
      productDetail,
      ...rest
    },
    ref
  ) {
    const pathname = usePathname();
    const [activeIndex, setActiveIndex] = useState(
      selectedImage || initialSlide
    );

    const mainSwiperRef = useRef<SwiperRef>(null);
    const [thumbsSwiper, setThumbsSwiper] = useState<SwiperClass | null>(null);

    useImperativeHandle(ref, () => mainSwiperRef.current as SwiperRef);

    const slideChangeHandler = (swiper: SwiperCore) => {
      setActiveIndex(swiper.activeIndex);

      if (onSelectImage) onSelectImage(swiper.activeIndex);
    };

    const swiperModules = hasThumbs
      ? [Thumbs, Pagination, Zoom, Scrollbar]
      : [Pagination, Zoom, Scrollbar];

    return (
      <div className="relative w-full max-w-[604px]">
        <div className="w-full">
          <Swiper
            zoom={true}
            scrollbar={{ hide: true }}
            pagination={true}
            modules={swiperModules}
            className={classNames(
              'mb-4 w-[604px] max-w-full bg-white lg:rounded-[20px]'
            )}
            ref={mainSwiperRef}
            thumbs={{
              swiper:
                thumbsSwiper && !thumbsSwiper.destroyed ? thumbsSwiper : null,
            }}
            initialSlide={activeIndex || initialSlide}
            onSlideChange={slideChangeHandler}
            spaceBetween={12}
            onClick={
              onSelectImage
                ? () => {
                    onSelectImage(activeIndex);
                  }
                : undefined
            }
            {...rest}
          >
            {images
              ? images?.map((image, index) => (
                  <SwiperSlide key={index} onClick={onOpenGallery}>
                    <div className="flex h-full w-full items-center justify-center ">
                      {outOfStock && (
                        <DiagonalLine className="!absolute left-0 top-0 z-50 bg-black-100/50 " />
                      )}
                      <Image
                        src={image.url || ''}
                        alt={productDetail.name || `Product image ${index + 1}`}
                        width={412}
                        height={412}
                        className={imageClassName}
                      />
                    </div>
                  </SwiperSlide>
                ))
              : []}
          </Swiper>
          {hasThumbs && (
            <div className="flex w-full items-center">
              <Swiper
                modules={[Thumbs]}
                grabCursor
                watchSlidesProgress
                slidesPerView={5}
                className="!mr-0 max-w-[calc(100%-84px)]"
                spaceBetween={20}
                onSwiper={setThumbsSwiper}
              >
                {images
                  ? images?.map((image, index) => (
                      <SwiperSlide
                        key={index}
                        className="!w-fit cursor-pointer"
                      >
                        <Image
                          src={image.url || ''}
                          alt={image.name || ''}
                          width={84}
                          height={84}
                          className={thumbClassName}
                        />
                      </SwiperSlide>
                    ))
                  : []}
              </Swiper>
              <button
                className="flex h-[36px] w-[36px] items-center justify-center rounded-lg border border-silver-400 bg-silver-100 p-2"
                onClick={() => {
                  thumbsSwiper?.slideNext();
                  mainSwiperRef.current?.swiper?.slideNext();
                }}
              >
                <ArrowLeftIcon className="text-silver-500" />
              </button>
            </div>
          )}
        </div>

        <ShareButton
          name={productDetail.name || ''}
          pathName={pathname}
          className="absolute right-3 top-3 z-50 h-8 w-8 lg:h-10 lg:w-10"
        />

        <DiscountContainer
          detail={`${activeIndex + 1}/${images.length}`}
          className="left-3 top-3 m-auto h-8 w-8 py-[6px] text-[10px] lg:h-10 lg:w-10 lg:rounded-[10px] lg:text-sm"
        />
        {discount !== 0 && (
          <DiscountContainer
            detail={`${discount}٪`}
            className="right-[8px] top-[265px] m-auto h-7 w-7 bg-black-500 px-1 py-[6px]  opacity-60"
          />
        )}
      </div>
    );
  }
);

export default SwiperImage;
